import { usableVideo } from '../../../../packages/domain/src/production-plan';
import { BUDGET_CAP, EXPERIMENT_IDEAS, MODELS } from '@drama/domain';
import type { CommandInput } from '@drama/domain';
import type { HomeView, ProjectDetail, ProjectView } from '../../../../packages/domain/src/view';
import { isBibleKind } from '../../../../packages/domain/src/view';
import {
  mockPitch,
  mockSeason,
  mockScript,
  mockQa,
} from '../../../../packages/domain/src/fixtures';
interface State {
  home: HomeView;
  details: Record<string, ProjectDetail>;
  members: { email: string; role: string; active: boolean }[];
}
const key = 'drama-lab-demo-v1';
const uuid = () => crypto.randomUUID();
function emptyProject(title: string, idea: string, genre: string): ProjectDetail {
  const p: ProjectView = {
    _id: uuid(),
    title,
    idea,
    genre,
    phase: 'IDEA',
    pitchVersion: 0,
    seasonVersion: 0,
    castLocked: false,
    visualDirection: 'Cinematic realism, warm practical light, intimate performances',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  return {
    project: p,
    bibles: [],
    characters: [],
    castings: [],
    actors: [],
    episodes: [],
    canon: [],
    shots: [],
    versions: [],
    commands: [],
    jobs: [],
    assets: [],
  };
}
function pitch(d: ProjectDetail, instruction = '') {
  const data = mockPitch(d.project.idea, d.project.genre, instruction);
  if (!EXPERIMENT_IDEAS.some((c) => c.idea === d.project.idea))
    data.title = d.project.title === 'Untitled series' ? 'A Story Between Us' : d.project.title;
  d.project = {
    ...d.project,
    title: data.title,
    phase: 'PITCH',
    pitchVersion: d.project.pitchVersion + 1,
  };
  d.bibles.push({ _id: uuid(), kind: 'pitch', version: d.project.pitchVersion, data });
  d.characters = data.characters.map((c) => ({
    _id: uuid(),
    projectId: d.project._id,
    key: c.id,
    data: c,
  }));
}
function season(d: ProjectDetail) {
  const p = d.bibles.filter(isBibleKind('pitch')).at(-1)!.data;
  const s = mockSeason(p);
  d.project.seasonVersion++;
  d.project.phase = 'CASTING';
  const version = d.project.seasonVersion;
  d.bibles.push({ _id: uuid(), kind: 'story', version, data: s.storyBible });
  d.bibles.push({ _id: uuid(), kind: 'visual', version, data: s.visualBible });
  d.bibles.push({ _id: uuid(), kind: 'season', version, data: s.episodes });
  d.canon = s.canon;
  d.episodes = s.episodes.map((e) => ({
    _id: uuid(),
    projectId: d.project._id,
    ...e,
    status: 'PLANNED',
    scriptVersion: 0,
    revision: 0,
  }));
}
function actor(d: ProjectDetail, characterId: string) {
  const c = d.characters.find((c) => c._id === characterId)!;
  const a = {
    _id: uuid(),
    name: c.data.name + ' · candidate',
    kind: 'custom' as const,
    projectId: d.project._id,
    description: c.data.appearance,
    voice: c.data.voice,
    identityAssetId: 'demo-portrait-' + c.key,
    locked: false,
  };
  d.actors.push(a);
  d.castings.push({ characterId, actorId: a._id });
}
function script(d: ProjectDetail, episodeId: string) {
  const ep = d.episodes.find((e) => e._id === episodeId)!;
  const s = mockScript();
  ep.scriptVersion++;
  ep.screenplay = s.screenplay;
  ep.status = 'SCRIPT_READY';
  d.shots.push(
    ...s.shots.map((spec) => ({
      _id: uuid(),
      projectId: d.project._id,
      episodeId,
      key: spec.key,
      spec,
      status: 'PLANNED',
      currentVersion: 0,
      scriptVersion: ep.scriptVersion,
    })),
  );
}
function initial(): State {
  const details: Record<string, ProjectDetail> = {};
  EXPERIMENT_IDEAS.forEach((c, i) => {
    const d = emptyProject(c.title, c.idea, c.genre);
    if (i < 3) pitch(d);
    if (i === 1) {
      season(d);
      d.characters.forEach((c) => actor(d, c._id));
      d.project.castLocked = true;
      d.project.phase = 'PRODUCTION';
      script(d, d.episodes[0]._id);
    }
    details[d.project._id] = d;
  });
  return {
    home: {
      access: { role: 'owner', email: 'director@dramalab.example', name: 'Director' },
      projects: Object.values(details).map((d) => d.project),
      budget: { cap: BUDGET_CAP, spent: 0, reserved: 0, suspended: false, storageBytes: 0 },
      workerOnline: false,
      commands: [],
    },
    details,
    members: [{ email: 'director@dramalab.example', role: 'owner', active: true }],
  };
}
let state: State;
try {
  state = JSON.parse(localStorage.getItem(key) ?? 'null') ?? initial();
} catch {
  state = initial();
}
// Persist seeded IDs before navigation so a direct series reload keeps its identity.
if (import.meta.env.VITE_DEMO_MODE === 'true') localStorage.setItem(key, JSON.stringify(state));
const listeners = new Set<() => void>();
function emit() {
  state = {
    ...state,
    home: {
      ...state.home,
      projects: Object.values(state.details).map((d) => d.project),
      library: Object.values(state.details).flatMap((d) => d.actors),
      commands: Object.values(state.details).flatMap((d) => d.commands),
    },
  };
  localStorage.setItem(key, JSON.stringify(state));
  for (const l of listeners) l();
}
export const demo = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot() {
    return state;
  },
  async create(a: { idea: string; genre: string; title?: string }) {
    if (a.idea.trim().length < 20)
      throw new Error('Give your story a little more detail (at least 20 characters).');
    const d = emptyProject(a.title || 'Untitled series', a.idea, a.genre);
    state.details[d.project._id] = d;
    emit();
    return d.project._id;
  },
  async request(c: CommandInput) {
    const d = state.details[c.projectId];
    if (d.commands.some((x) => x._id === c.requestId)) return;
    const command = {
      _id: c.requestId,
      projectId: c.projectId,
      episodeId: c.episodeId,
      kind: c.kind,
      status: 'RUNNING',
      createdAt: Date.now(),
    };
    d.commands.push(command);
    emit();
    await new Promise((r) => setTimeout(r, 600));
    if (command.status === 'CANCELLED') return;
    if (c.kind === 'DEVELOP' || c.kind === 'REVISE_PITCH') pitch(d, c.instruction);
    if (c.kind === 'PLAN_SEASON') season(d);
    if (c.kind === 'CAST_ACTOR') actor(d, c.characterId!);
    if (c.kind === 'PLAN_EPISODE') script(d, c.episodeId!);
    if (c.kind === 'PRODUCE_EPISODE' || c.kind === 'REGENERATE_SHOT') {
      const ep = d.episodes.find((e) => e._id === c.episodeId)!;
      ep.currentMasterId = undefined;
      ep.status = 'CREATOR_REVIEW';
      if (!d.assets.some((a) => a._id === 'demo-video'))
        d.assets.push({ _id: 'demo-video', key: 'demo-video', mime: 'video/mp4' });
      for (const s of d.shots.filter(
        (s) =>
          s.episodeId === ep._id &&
          s.scriptVersion === ep.scriptVersion &&
          (!c.shotId || s._id === c.shotId),
      )) {
        if (
          c.kind === 'PRODUCE_EPISODE' &&
          usableVideo(
            d.versions.find((v) => v.shotId === s._id && v.version === s.currentVersion),
            d.assets,
            d.project.seasonVersion,
          )
        )
          continue;
        s.currentVersion++;
        s.status = 'CREATOR_REVIEW';
        if (c.instruction) s.spec = { ...s.spec, action: s.spec.action + ' ' + c.instruction };
        d.versions.push({
          _id: uuid(),
          shotId: s._id,
          version: s.currentVersion,
          spec: { ...s.spec },
          referenceAssetId: 'demo-frame',
          videoAssetId: 'demo-video',
          qaReference: mockQa,
          qaVideo: mockQa,
          humanAccepted: false,
        });
      }
    }
    if (c.kind === 'REVISE_EPISODE') {
      const ep = d.episodes.find((e) => e._id === c.episodeId)!;
      const shot = d.shots.filter((s) => s.episodeId === ep._id).at(-1)!;
      shot.spec = { ...shot.spec, action: c.instruction ?? shot.spec.action };
      shot.status = 'PLANNED';
      shot.currentVersion++;
      ep.currentMasterId = undefined;
    }
    if (c.kind === 'ADD_NARRATION') {
      const ep = d.episodes.find((e) => e._id === c.episodeId)!;
      ep.narrationAssetId = 'demo-narration';
      ep.currentMasterId = undefined;
    }
    if (c.kind === 'ASSEMBLE_EPISODE' || (c.kind === 'PRODUCE_EPISODE' && c.assembleWhenReady)) {
      const ep = d.episodes.find((e) => e._id === c.episodeId)!;
      ep.currentMasterId = 'demo-master-' + uuid();
      ep.status = 'CREATOR_REVIEW';
      d.project.phase = 'REVIEW';
    }
    command.status = 'SUCCEEDED';
    d.jobs.push({
      _id: uuid(),
      commandId: command._id,
      kind: c.kind,
      status: 'SUCCEEDED',
      model: c.videoModel ?? MODELS.text,
      reservation: 0,
      output: { costMicros: 0, costBasis: 'simulated' },
    });
    emit();
  },
  async lockCast(projectId: string) {
    const d = state.details[projectId];
    if (d.characters.some((c) => !d.castings.some((x) => x.characterId === c._id)))
      throw new Error('Cast every character first.');
    d.project.castLocked = true;
    d.project.phase = 'PRODUCTION';
    emit();
  },
  async promoteActor(actorId: string) {
    for (const d of Object.values(state.details)) {
      const a = d.actors.find((a) => a._id === actorId);
      if (a) a.kind = 'platform';
    }
    emit();
  },
  async setDirection(projectId: string, direction: string) {
    state.details[projectId].project.visualDirection = direction;
    emit();
  },
  async castExisting(projectId: string, characterId: string, actorId: string) {
    state.details[projectId].castings.push({ characterId, actorId });
    emit();
  },
  async acceptShot(shotId: string) {
    for (const d of Object.values(state.details)) {
      const s = d.shots.find((s) => s._id === shotId);
      if (s) {
        s.acceptedVersion = s.currentVersion;
        s.status = 'ACCEPTED';
        const v = d.versions.find((v) => v.shotId === s._id && v.version === s.currentVersion);
        if (v) v.humanAccepted = true;
      }
    }
    emit();
  },
  async acceptEpisode(episodeId: string, expectedMasterId?: string) {
    for (const d of Object.values(state.details)) {
      const e = d.episodes.find((e) => e._id === episodeId);
      if (e) {
        if (expectedMasterId && expectedMasterId !== e.currentMasterId)
          throw new Error('The episode changed.');
        if (expectedMasterId) {
          for (const shot of d.shots.filter(
            (s) => s.episodeId === e._id && s.scriptVersion === e.scriptVersion,
          )) {
            const version = d.versions.find(
              (v) => v.shotId === shot._id && v.version === shot.currentVersion,
            );
            if (!version?.qaVideo?.passed) throw new Error('Resolve QA before approval.');
            shot.acceptedVersion = shot.currentVersion;
            shot.status = 'ACCEPTED';
            version.humanAccepted = true;
          }
        }
        if (d.shots.some((s) => s.episodeId === e._id && s.acceptedVersion !== s.currentVersion))
          throw new Error('Accept each shot before approving the episode.');
        e.acceptedMasterId = e.currentMasterId;
        e.status = 'ACCEPTED';
        d.project.phase = 'ACCEPTED';
      }
    }
    emit();
  },
  async cancel(commandId: string) {
    for (const d of Object.values(state.details)) {
      const c = d.commands.find((c) => c._id === commandId);
      if (c) c.status = 'CANCELLED';
    }
    emit();
  },
  async url(assetId: string) {
    return assetId.includes('portrait') ? '' : assetId.includes('frame') ? '' : '/demo/preview.mp4';
  },
  async review() {
    return;
  },
  async seedConcepts() {
    if (Object.keys(state.details).length) throw new Error('Your Lab already has concepts.');
    state = initial();
    emit();
  },
  async setMember(a: { email: string; role: string; active: boolean }) {
    state.members = state.members.filter((m) => m.email !== a.email);
    state.members.push(a);
    emit();
  },
  async reconcile() {
    return;
  },
  reset() {
    state = initial();
    emit();
  },
};
